import { jwtDecode } from "jwt-decode";
import { matchPath } from "react-router-dom";

/**
 * Extract a JWT string from cookie/context (string, JSON-wrapped, or `{ token }` object).
 */
export function normalizeAuthTokenForDecode(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("{") || t.startsWith("[")) {
      try {
        const o = JSON.parse(t);
        if (typeof o === "string") return o;
        return o?.token ?? o?.accessToken ?? o?.access_token ?? null;
      } catch {
        return raw;
      }
    }
    return t;
  }
  if (typeof raw === "object") {
    return raw?.token ?? raw?.accessToken ?? raw?.access_token ?? null;
  }
  return null;
}

/**
 * Resolve role from JWT; supports `user.role` and top-level `role` (staging may differ).
 */
export function getRoleFromAuthToken(authToken) {
  const jwtStr = normalizeAuthTokenForDecode(authToken);
  if (!jwtStr || typeof jwtStr !== "string") return null;
  try {
    const p = jwtDecode(jwtStr);
    const r = p?.user?.role ?? p?.role;
    return r === undefined || r === null ? null : r;
  } catch {
    return null;
  }
}

/**
 * Paths that may be used with `RequireAuth` by non-`admin` JWT roles (`user`, future partner roles).
 * Anything else is Magic Furnish master-admin / internal tooling and must be denied on the client.
 *
 * Security: server APIs must still enforce authorization — the UI cannot be the only gate.
 */
const NON_ADMIN_ALLOWED_PATHS = [
  "/main",
  "/floor-plan/:layoutId/version/:version",
  "/floor-gallery/:galleryId",
  "/upload-floor",
  "/layout/:id",
  "/gallery/:id",
];

export function isAllowedPathForNonAdminRoles(pathname) {
  if (!pathname) return false;
  return NON_ADMIN_ALLOWED_PATHS.some((path) =>
    matchPath({ path, end: true }, pathname)
  );
}

export function isMasterAdminJwtRole(role) {
  return role === "admin";
}

export function isPartnerAdminJwtRole(role) {
  return role === "partnerAdmin";
}
